import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Eye, MessageCircle, Mail } from "lucide-react";

const STATUS = [
  { v: "new", c: "bg-teal-100 text-teal-800" },
  { v: "contacted", c: "bg-blue-100 text-blue-800" },
  { v: "quoted", c: "bg-indigo-100 text-indigo-800" },
  { v: "closed", c: "bg-slate-100 text-slate-700" },
];

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () => api.get("/enquiries").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? items : items.filter((x) => x.status === filter);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/enquiries/${id}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const saveNotes = async () => {
    try { await api.patch(`/enquiries/${selected.id}`, { notes }); toast.success("Notes saved"); load(); setSelected(null); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete enquiry?")) return;
    await api.delete(`/enquiries/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>Enquiries</h1>
          <p className="mt-2 text-sm text-slate-500">{items.length} total · {items.filter((i) => i.status === "new").length} new</p>
        </div>
        <div className="flex gap-2">
          {["all", "new", "contacted", "quoted", "closed"].map((s) => (
            <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
              className={filter === s ? "bg-[#0F2942] text-white hover:bg-[#0A192F]" : ""}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((e) => {
              const st = STATUS.find((s) => s.v === e.status) || STATUS[0];
              return (
                <TableRow key={e.id} data-testid={`enq-row-${e.id}`}>
                  <TableCell className="font-medium text-[#0F2942]">{e.name}</TableCell>
                  <TableCell className="text-slate-600">{e.organization || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-600">{e.email}<br/>{e.phone || ""}</TableCell>
                  <TableCell className="text-xs">{e.product_name || "-"}</TableCell>
                  <TableCell>
                    <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                      <SelectTrigger className="h-8 w-32 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <a href={`mailto:${e.email}`} className="inline-flex"><Button size="sm" variant="ghost"><Mail className="h-4 w-4" /></Button></a>
                    {e.phone && <a href={`https://wa.me/${e.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="ghost"><MessageCircle className="h-4 w-4 text-emerald-600" /></Button></a>}
                    <Button size="sm" variant="ghost" onClick={() => { setSelected(e); setNotes(e.notes || ""); }} data-testid={`view-${e.id}`}><Eye className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(e.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-500">No enquiries</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#0F2942]">{selected?.name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div><b>Organization:</b> {selected.organization || "-"}</div>
              <div><b>Email:</b> {selected.email}</div>
              <div><b>Phone:</b> {selected.phone || "-"}</div>
              {selected.product_name && <div><b>Product:</b> {selected.product_name}</div>}
              <div className="rounded-lg bg-slate-50 p-3"><b>Message:</b><br/>{selected.message}</div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Internal Notes</label>
                <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="enq-notes" />
              </div>
              <Button onClick={saveNotes} className="w-full bg-[#0F2942] text-white hover:bg-[#0A192F]">Save Notes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
